import React, { createContext, useContext, useState, ReactNode } from 'react';
import { PricingPlan } from './PricingContext';
import type { CreateOrderRequest, BusinessOrder } from '../services/businessApi';

export interface BusinessDetails {
  country: string;
  countryCode: string;
  flag: string;
  selectedPlan?: string;
  selectedPlanId?: string;
  selectedPricingPlan?: PricingPlan;
  siteName?: string;
  theme?: string;
  otpCode?: string;
  orderId?: string;
  paymentNetworkId?: string;
  paymentNetworkName?: string;
  paymentNetworkSymbol?: string;
  paymentNetworkChain?: string;
  paymentWalletAddress?: string;
  paymentTxHash?: string;
}

const parsePriceToNumber = (raw?: string): number | undefined => {
  if (!raw) return undefined;
  const normalized = raw.replace(/[^0-9.]/g, '');
  if (!normalized) return undefined;
  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export interface CollectedBusinessData {
  country: string;
  countryCode: string;
  flag: string;
  selectedPlan: string;
  selectedPlanId: string;
  siteName: string;
  theme?: string;
  otpCode?: string;
  orderId?: string;
  paymentNetworkId?: string;
  paymentNetworkName?: string;
  paymentNetworkSymbol?: string;
  paymentNetworkChain?: string;
  paymentWalletAddress?: string;
  paymentTxHash?: string;
  submittedAt: string;
  status: 'draft' | 'submitted' | 'completed';
}

interface BusinessContextType {
  businessDetails: BusinessDetails | null;
  setBusinessDetails: (details: BusinessDetails) => void;
  clearBusinessDetails: () => void;
  collectedData: CollectedBusinessData | null;
  setCollectedData: (data: CollectedBusinessData) => void;
  isDialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  isCountryDetailsDialogOpen: boolean;
  openCountryDetailsDialog: () => void;
  closeCountryDetailsDialog: () => void;
  isPricingDialogOpen: boolean;
  openPricingDialog: () => void;
  closePricingDialog: () => void;
  isBusinessSetupDialogOpen: boolean;
  openBusinessSetupDialog: () => void;
  closeBusinessSetupDialog: () => void;
  isWebsiteStudioDialogOpen: boolean;
  openWebsiteStudioDialog: () => void;
  closeWebsiteStudioDialog: () => void;
  isOrderDetailsDialogOpen: boolean;
  openOrderDetailsDialog: () => void;
  closeOrderDetailsDialog: () => void;
  isPaymentDialogOpen: boolean;
  openPaymentDialog: () => void;
  closePaymentDialog: () => void;
  isThemeStudioDialogOpen: boolean;
  openThemeStudioDialog: () => void;
  closeThemeStudioDialog: () => void;
  closeAllDialogs: () => void;
  submitBusinessData: (payloadOverrides?: Partial<CreateOrderRequest>) => Promise<void>;
  restoreOrderDetails: (order: BusinessOrder, pricingPlan?: PricingPlan) => void;
  isLoading: boolean;
  currentMessage: string;
  setCurrentMessage: (message: string) => void;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const useBusinessContext = () => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusinessContext must be used within a BusinessProvider');
  }
  return context;
};

interface BusinessProviderProps {
  children: ReactNode;
}

export const BusinessProvider: React.FC<BusinessProviderProps> = ({ children }) => {
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails | null>(null);
  const [collectedData, setCollectedData] = useState<CollectedBusinessData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCountryDetailsDialogOpen, setIsCountryDetailsDialogOpen] = useState(false);
  const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
  const [isBusinessSetupDialogOpen, setIsBusinessSetupDialogOpen] = useState(false);
  const [isWebsiteStudioDialogOpen, setIsWebsiteStudioDialogOpen] = useState(false);
  const [isOrderDetailsDialogOpen, setIsOrderDetailsDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isThemeStudioDialogOpen, setIsThemeStudioDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);
  
  const openCountryDetailsDialog = () => setIsCountryDetailsDialogOpen(true);
  const closeCountryDetailsDialog = () => setIsCountryDetailsDialogOpen(false);

  const openPricingDialog = () => setIsPricingDialogOpen(true);
  const closePricingDialog = () => setIsPricingDialogOpen(false);

  const openBusinessSetupDialog = () => setIsBusinessSetupDialogOpen(true);
  const closeBusinessSetupDialog = () => setIsBusinessSetupDialogOpen(false);

  const openWebsiteStudioDialog = () => setIsWebsiteStudioDialogOpen(true);
  const closeWebsiteStudioDialog = () => setIsWebsiteStudioDialogOpen(false);

  const openOrderDetailsDialog = () => setIsOrderDetailsDialogOpen(true);
  const closeOrderDetailsDialog = () => setIsOrderDetailsDialogOpen(false);

  const openPaymentDialog = () => setIsPaymentDialogOpen(true);
  const closePaymentDialog = () => setIsPaymentDialogOpen(false);

  const openThemeStudioDialog = () => setIsThemeStudioDialogOpen(true);
  const closeThemeStudioDialog = () => setIsThemeStudioDialogOpen(false);

  const closeAllDialogs = () => {
    setIsDialogOpen(false);
    setIsCountryDetailsDialogOpen(false);
    setIsPricingDialogOpen(false);
    setIsBusinessSetupDialogOpen(false);
    setIsWebsiteStudioDialogOpen(false);
    setIsOrderDetailsDialogOpen(false);
    setIsPaymentDialogOpen(false);
    setIsThemeStudioDialogOpen(false);
  };

  const clearBusinessDetails = () => {
    setBusinessDetails(null);
    setCollectedData(null);
  };

  const submitBusinessData = async (payloadOverrides?: Partial<CreateOrderRequest>) => {
    if (!businessDetails) return;
    if (businessDetails.orderId) {
      setCurrentMessage('Order already created.');
      return;
    }
    
    setIsLoading(true);
    setCurrentMessage('Submitting your business data...');
    
    try {
      const { businessApi } = await import('../services/businessApi');
      
        // Ensure we have plan details
        const selectedPlanName = businessDetails.selectedPricingPlan?.name || businessDetails.selectedPlan || '';
        const selectedPlanId = businessDetails.selectedPricingPlan?.id || businessDetails.selectedPlanId || '';
        
        if (!selectedPlanName || !selectedPlanId) {
          throw new Error('Selected plan information is missing. Please select a plan again.');
        }

        const orderData: CreateOrderRequest = {
          country: businessDetails.country,
          country_code: businessDetails.countryCode,
          flag: businessDetails.flag,
          selected_plan: selectedPlanName,
          selected_plan_id: selectedPlanId,
          site_name: businessDetails.siteName || '',
          theme: businessDetails.theme,
          otp_code: businessDetails.otpCode,
          notes: `Order submitted from ${businessDetails.country}. Package: ${selectedPlanName} (${selectedPlanId}). Price: ${businessDetails.selectedPricingPlan?.priceDisplay || businessDetails.selectedPricingPlan?.setupFee?.amount || 'N/A'}`
        };

        const inferredTotalAmount =
          parsePriceToNumber(businessDetails.selectedPricingPlan?.priceDisplay) ??
          parsePriceToNumber(businessDetails.selectedPricingPlan?.setupFee?.amount);

        if (inferredTotalAmount !== undefined) {
          orderData.total_amount = inferredTotalAmount;
        }
        if (businessDetails.selectedPricingPlan?.currency) {
          orderData.currency = businessDetails.selectedPricingPlan.currency;
        }
        if (businessDetails.paymentNetworkId) {
          orderData.payment_network_id = businessDetails.paymentNetworkId;
        }
        if (businessDetails.paymentNetworkName) {
          orderData.payment_network_name = businessDetails.paymentNetworkName;
        }
        if (businessDetails.paymentNetworkSymbol) {
          orderData.payment_network_symbol = businessDetails.paymentNetworkSymbol;
        }
        if (businessDetails.paymentNetworkChain) {
          orderData.payment_network_chain = businessDetails.paymentNetworkChain;
        }
        if (businessDetails.paymentWalletAddress) {
          orderData.payment_wallet_address = businessDetails.paymentWalletAddress;
        }
        if (businessDetails.paymentTxHash) {
          orderData.payment_tx_hash = businessDetails.paymentTxHash;
        }
        if (payloadOverrides) {
          Object.assign(orderData, payloadOverrides);
        }

      const result = await businessApi.createOrder(orderData);
      
      if (result.success && result.data) {
        const collectedData: CollectedBusinessData = {
          country: businessDetails.country,
          countryCode: businessDetails.countryCode,
          flag: businessDetails.flag,
          selectedPlan: selectedPlanName,
          selectedPlanId: selectedPlanId,
          siteName: businessDetails.siteName || '',
          theme: businessDetails.theme,
          otpCode: businessDetails.otpCode,
          orderId: result.data.order.id,
          paymentNetworkId: businessDetails.paymentNetworkId,
          paymentNetworkName: businessDetails.paymentNetworkName,
          paymentNetworkSymbol: businessDetails.paymentNetworkSymbol,
          paymentNetworkChain: businessDetails.paymentNetworkChain,
          paymentWalletAddress: businessDetails.paymentWalletAddress,
          paymentTxHash: businessDetails.paymentTxHash,
          submittedAt: new Date().toISOString(),
          status: 'submitted'
        };
        setCollectedData(collectedData);
        setBusinessDetails({
          ...businessDetails,
          orderId: result.data.order.id
        });
        setCurrentMessage('Business data submitted successfully!');
      } else {
        setCurrentMessage(result.message || 'Failed to submit data. Please try again.');
      }
    } catch (error) {
      setCurrentMessage('An error occurred. Please try again.');
      console.error('Error submitting business data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const restoreOrderDetails = (order: BusinessOrder, pricingPlan?: PricingPlan) => {
    const details: BusinessDetails = {
      country: order.country,
      countryCode: order.country_code,
      flag: order.flag,
      selectedPlan: order.selected_plan,
      selectedPlanId: order.selected_plan_id,
      selectedPricingPlan: pricingPlan,
      siteName: order.site_name,
      theme: order.theme,
      otpCode: order.otp_code,
      orderId: order.id,
      paymentNetworkId: order.payment_network_id,
      paymentNetworkName: order.payment_network_name,
      paymentNetworkSymbol: order.payment_network_symbol,
      paymentNetworkChain: order.payment_network_chain,
      paymentWalletAddress: order.payment_wallet_address,
      paymentTxHash: order.payment_tx_hash,
    };
    setBusinessDetails(details);
  };

  const value: BusinessContextType = {
    businessDetails,
    setBusinessDetails,
    clearBusinessDetails,
    collectedData,
    setCollectedData,
    isDialogOpen,
    openDialog,
    closeDialog,
    isCountryDetailsDialogOpen,
    openCountryDetailsDialog,
    closeCountryDetailsDialog,
    isPricingDialogOpen,
    openPricingDialog,
    closePricingDialog,
    isBusinessSetupDialogOpen,
    openBusinessSetupDialog,
    closeBusinessSetupDialog,
    isWebsiteStudioDialogOpen,
    openWebsiteStudioDialog,
    closeWebsiteStudioDialog,
    isOrderDetailsDialogOpen,
    openOrderDetailsDialog,
    closeOrderDetailsDialog,
    isPaymentDialogOpen,
    openPaymentDialog,
    closePaymentDialog,
    isThemeStudioDialogOpen,
    openThemeStudioDialog,
    closeThemeStudioDialog,
    closeAllDialogs,
    submitBusinessData,
    restoreOrderDetails,
    isLoading,
    currentMessage,
    setCurrentMessage,
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};

